import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { AIImportQuestion } from '@/types/ai-import';

/** Render a question's proposed value (jsonb of unknown shape) as readable text. */
export function questionValueText(v: unknown): string | null {
  if (v == null || v === '') return null;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const inner = o.value ?? o.name ?? null;
    return inner == null ? JSON.stringify(v) : String(inner);
  }
  return String(v);
}

/**
 * The value "Accept" commits for a question: suggested_value when it renders as
 * text, else current_value — whichever one the user actually SEES. Shared by
 * the row's Accept button and the walkthrough's Accept-all so they can't drift.
 */
export function questionAcceptValue(question: AIImportQuestion): unknown {
  return questionValueText(question.suggested_value) != null
    ? question.suggested_value
    : question.current_value;
}

/**
 * Some questions are a yes/no on a whole record rather than "is this field
 * value right?" — "…names Crystal (Lamb Companies) as property manager … Add to
 * contacts?" is answered Yes or No, and offering Accept / Correct… / Dismiss
 * for it asks the reviewer to translate. These rows get Yes / No buttons and
 * keep a narrow escape hatch for fixing the person's name before adding them.
 */
export function isYesNoQuestion(question: AIImportQuestion): boolean {
  return question.target_entity === 'contact';
}

// Title-Case labels + distinct tones so a dismissed question no longer wears the
// same green "success" badge as a resolved one, and raw enum text stops leaking.
const STATUS_BADGE: Record<string, { label: string; variant: 'yellow' | 'green' | 'gray' }> = {
  pending: { label: 'Pending', variant: 'yellow' },
  resolved: { label: 'Resolved', variant: 'green' },
  dismissed: { label: 'Dismissed', variant: 'gray' },
};

/**
 * A proposed contact reads as a person, not as the name alone: the reviewer is
 * deciding whether a stranger belongs in the directory, and the role, employer
 * and reachable channels are what make that decision answerable.
 */
function ContactDetails({ value }: { value: unknown }) {
  if (!value || typeof value !== 'object') return null;
  const c = value as Record<string, unknown>;
  const str = (k: string): string => (typeof c[k] === 'string' ? (c[k] as string).trim() : '');
  const role = str('role');
  const facts = [str('organization'), str('email'), str('phone')].filter(Boolean);
  if (!role && facts.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
      {role && (
        <Badge size="sm" variant="gray">
          {role.replace(/_/g, ' ')}
        </Badge>
      )}
      {facts.length > 0 && (
        <span className="text-gray-600 dark:text-gray-400">{facts.join(' · ')}</span>
      )}
    </div>
  );
}

/**
 * One question row: shows WHAT was proposed for the field (Ryan couldn't see it
 * before), lets him accept it, type a correction, or dismiss. Resolving writes
 * the ai_import_questions row; the correction is folded into the extraction on
 * the next assembly (Re-analyze / Continue re-run), same path as email replies.
 */
export function QuestionRow({
  question,
  disabled,
  onResolve,
  onDismiss,
}: {
  question: AIImportQuestion;
  disabled: boolean;
  onResolve: (resolvedValue: unknown) => void;
  onDismiss: () => void;
}) {
  // The displayed value can come from suggested_value OR fall back to
  // current_value. Accept must resolve with whichever one the user actually sees —
  // resolving with suggested_value when current_value supplied the text would
  // record a different (often null) value than shown.
  const proposed =
    questionValueText(question.suggested_value) ?? questionValueText(question.current_value);
  const acceptValue = questionAcceptValue(question);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(proposed ?? '');
  const isPending = question.status === 'pending';
  const badge = STATUS_BADGE[question.status] ?? { label: question.status, variant: 'gray' as const };
  const draftEmpty = draft.trim() === '';
  const yesNo = isYesNoQuestion(question);

  return (
    <li className="border rounded-lg p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100">{question.question_text}</div>
          {/* The proposed value — the thing that was invisible before. */}
          <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {question.target_field && <span className="text-gray-400 dark:text-gray-500">{question.target_field}: </span>}
            {proposed ? (
              <span className="font-medium">{proposed}</span>
            ) : (
              <span className="italic text-gray-400 dark:text-gray-500">no value extracted</span>
            )}
          </div>
          {question.question_type === 'contact_review' && (
            <ContactDetails value={question.suggested_value} />
          )}
          {question.context_text && (
            <div className="mt-1 text-xs text-gray-500 italic line-clamp-2 dark:text-gray-400">from: {question.context_text}</div>
          )}

          {editing ? (
            <div className="mt-2">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !disabled && !draftEmpty) {
                      onResolve(draft.trim());
                      setEditing(false);
                    }
                  }}
                  placeholder={yesNo ? 'Correct name…' : 'Correct value (or “TBD”, “unknown”)…'}
                  className="h-8 w-56"
                />
                <Button
                  size="sm"
                  disabled={disabled || draftEmpty}
                  onClick={() => {
                    onResolve(draft.trim());
                    setEditing(false);
                  }}
                >
                  Save correction
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(proposed ?? ''); }}>
                  Cancel
                </Button>
              </div>
              {draftEmpty && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {yesNo ? 'Enter a name, or answer No instead.' : 'Enter a value, or Dismiss instead.'}
                </p>
              )}
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              {/* A yes/no question gets two equal answers — No is an answer, not
                  a way to duck the question — plus a narrow escape hatch for a
                  misread name. */}
              <Button size="sm" variant="outline" disabled={disabled || !isPending} onClick={() => onResolve(acceptValue)}>
                {yesNo ? 'Yes' : proposed ? 'Accept' : 'Confirm'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={disabled || !isPending}
                onClick={yesNo ? onDismiss : () => setEditing(true)}
              >
                {yesNo ? 'No' : 'Correct…'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled || !isPending}
                onClick={yesNo ? () => setEditing(true) : onDismiss}
              >
                {yesNo ? 'Fix name…' : 'Dismiss'}
              </Button>
            </div>
          )}
        </div>
        <Badge size="sm" variant={badge.variant}>
          {badge.label}
        </Badge>
      </div>
    </li>
  );
}
